import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

const DocumentComparison = () => {
  const { t } = useTranslation('home');
  const [transactions, setTransactions] = useState([
    { date: '10/01', desc: 'Walmart', amount: -150 },
    { date: '10/03', desc: 'ATM Withdrawal', amount: -300 },
    { date: '10/03', desc: 'Debt Collection', amount: -900 },
    { date: '10/16', desc: 'Welfare Benefits', amount: 1250 },
    { date: '10/20', desc: 'Amazon.com', amount: -750 },
    { date: '10/21', desc: 'Kroger', amount: -250 },
    { date: '10/28', desc: 'Shell Oil', amount: -60 }
  ]);
  const [balance, setBalance] = useState(240);
  const [isEditing, setIsEditing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [modificationMessage, setModificationMessage] = useState(null);
  const [highlightedRow, setHighlightedRow] = useState(null);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [isDocumentVisible, setIsDocumentVisible] = useState(true);

  // Animation sequence control
  useEffect(() => {
    const startAnimation = (entries) => {
      if (entries[0].isIntersecting && !isEditing) {
        setIsEditing(true);
      }
    };

    const observer = new IntersectionObserver(startAnimation, {
      threshold: 0.5,
    });

    const element = document.getElementById('document-comparison');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [isEditing]);

  // Show modification message with animation
  const showModification = (message) => {
    setModificationMessage(message);
    setTimeout(() => setModificationMessage(null), 3000);
  };

  // Animation steps sequence
  useEffect(() => {
    if (!isEditing) return;

    const steps = [
      // Step 1: Change both description and amount for Welfare Benefits
      () => {
        setHighlightedRow('Welfare Benefits');
        setTimeout(() => {
          setTransactions(prev => prev.map(t => 
            t.desc === 'Welfare Benefits' 
              ? { ...t, desc: 'Salary Payment', amount: 7500 }
              : t
          ));
          showModification(t('pdfeditor.modifying'));
        }, 1000);
      },
      // Step 2: Change both description and amount for Debt Collection
      () => {
        setHighlightedRow('Debt Collection');
        setTimeout(() => {
          setTransactions(prev => prev.map(t => 
            t.desc === 'Debt Collection' 
              ? { ...t, desc: 'Charity Donation', amount: -100 }
              : t
          ));
          showModification(t('pdfeditor.modifying'));
        }, 1000);
      },
      // Step 3: Change balance to 24,000
      () => {
        setHighlightedRow('balance');
        setTimeout(() => {
          setBalance(24000);
          showModification(t('pdfeditor.balanceModified'));
          
          // Show saved message after a longer delay
          setTimeout(() => {
            setShowSavedMessage(true);
            
            // Hide saved message and start document transition
            setTimeout(() => {
              setShowSavedMessage(false);
              
              // Fade out the document
              setIsDocumentVisible(false);
              
              // Wait for fade out, then reset and show document
              setTimeout(() => {
                // Reset to original values
                setTransactions([
                  { date: '10/01', desc: 'Walmart', amount: -150 },
                  { date: '10/03', desc: 'ATM Withdrawal', amount: -300 },
                  { date: '10/03', desc: 'Debt Collection', amount: -900 },
                  { date: '10/16', desc: 'Welfare Benefits', amount: 1250 },
                  { date: '10/20', desc: 'Amazon.com', amount: -750 },
                  { date: '10/21', desc: 'Kroger', amount: -250 },
                  { date: '10/28', desc: 'Shell Oil', amount: -60 }
                ]);
                setBalance(240);
                setCurrentStep(0);
                
                // Show document again
                setTimeout(() => {
                  setIsDocumentVisible(true);
                  setModificationMessage(null);
                }, 1000);
              }, 2000);
            }, 3000);
          }, 3000);
        }, 1000);
      }
    ];

    if (currentStep < steps.length) {
      const timer = setTimeout(() => {
        steps[currentStep]();
        // Clear highlight after 4 seconds
        setTimeout(() => setHighlightedRow(null), 4000);
        setCurrentStep(prev => prev + 1);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isEditing, currentStep, t]);

  // Update the transaction row rendering to include highlighting
  const getRowClassName = (transaction) => {
    const isHighlighted = 
      highlightedRow === transaction.desc || 
      (transaction.desc === 'Salary Payment' && highlightedRow === 'Welfare Benefits') ||
      (transaction.desc === 'Charity Donation' && highlightedRow === 'Debt Collection');
    
    return `grid grid-cols-[45px_1fr_80px] sm:grid-cols-[60px_1fr_100px] gap-2 p-2 rounded transition-colors duration-300
      ${isHighlighted ? 'bg-yellow-100 dark:bg-yellow-900/30' : ''}`;
  };

  // Update the balance display to include highlighting
  const getBalanceClassName = () => {
    return `px-4 py-3 rounded-lg flex justify-between transition-colors duration-300
      ${highlightedRow === 'balance' ? 'bg-yellow-100 dark:bg-yellow-900/30' : 'bg-gray-100 dark:bg-gray-700'}`;
  };

  return (
    <div id="document-comparison" className="relative w-full max-w-3xl mx-auto">
      {/* PDF Editor Window Frame */}
      <div className="bg-gray-100 dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* Window Title Bar */}
        <div className="bg-gray-200 dark:bg-gray-800 px-3 sm:px-4 py-1.5 sm:py-2 flex items-center justify-between border-b border-gray-300 dark:border-gray-600">
          {/* Window Controls */}
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></div>
            <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
          </div>
          
          {/* File Name */}
          <div className="flex items-center space-x-1.5 sm:space-x-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            <svg className="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 22H17C18.6569 22 20 20.6569 20 19V9L15 4H7C5.34315 4 4 5.34315 4 7V19C4 20.6569 5.34315 22 7 22Z" stroke="currentColor" strokeWidth="2"/>
              <path d="M14 4V10H20" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span>{t('pdfeditor.title')}</span>
          </div>
          
          <div className="w-4 sm:w-16"></div> {/* Spacer for alignment */}
        </div>

        {/* Toolbar */}
        <div className="bg-gray-50 dark:bg-gray-800/50 px-2 sm:px-4 py-1 sm:py-1.5 border-b border-gray-200 dark:border-gray-700 flex items-center space-x-2 sm:space-x-4">
          <button className="p-1 sm:p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded">
            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
          <button className="p-1 sm:p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded">
            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>
          <button className="p-1 sm:p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded">
            <svg className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <div className="h-3 sm:h-4 w-px bg-gray-300 dark:bg-gray-600"></div>
          <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{t('pdfeditor.pageCount')}</span>
        </div>

        {/* Document Viewer Area */}
        <div className="bg-gray-300 dark:bg-gray-950 p-4 sm:p-8 min-h-[400px] sm:min-h-[600px] overflow-auto relative">
          {/* Document Page with fade transition */}
          <motion.div
            animate={{ opacity: isDocumentVisible ? 1 : 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white dark:bg-gray-800 w-full max-w-2xl mx-auto rounded-lg shadow-lg p-4 sm:p-8"
          >
            {/* Bank Statement Header */}
            <div className="mb-4 sm:mb-8">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-700 dark:text-gray-300" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M11.5,1L2,6V8H21V6M16,10V17H19V10M2,22H21V19H2M10,10V17H13V10M4,10V17H7V10H4Z" />
                </svg>
                <div>
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{t('pdfeditor.bankStatement')}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Global Bank Corporation</p>
                </div>
              </div>

              {/* Statement Info Grid */}
              <div className="grid grid-cols-2 gap-3 sm:gap-6 text-xs sm:text-sm">
                <div className="space-y-1">
                  <div className="text-gray-600 dark:text-gray-400">Account Holder:</div>
                  <div className="font-medium text-gray-900 dark:text-white">John Doe</div>
                </div>
                <div className="space-y-1">
                  <div className="text-gray-600 dark:text-gray-400">Account Number:</div>
                  <div className="font-medium text-gray-900 dark:text-white">****7890</div>
                </div>

              </div>

              {/* Divider */}
              <div className="h-px bg-gray-200 dark:bg-gray-700 my-4"></div>
            </div>

            {/* Modification Message - Repositioned */}
            <AnimatePresence>
              {modificationMessage && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="absolute left-1/2 bottom-24 transform -translate-x-1/2 z-50"
                >
                  <div className="bg-yellow-50 dark:bg-yellow-900/50 border border-yellow-200 dark:border-yellow-800 rounded-lg px-4 py-2 shadow-lg">
                    <div className="flex items-center space-x-2">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        {modificationMessage}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Saved Message - Repositioned */}
            <AnimatePresence>
              {showSavedMessage && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="absolute left-1/2 bottom-24 transform -translate-x-1/2 z-50"
                >
                  <div className="bg-green-100 dark:bg-green-900/50 border border-green-200 dark:border-green-800 rounded-lg px-4 py-2 shadow-lg">
                    <div className="flex items-center space-x-2">
                      <CheckIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                      <span className="text-sm font-medium text-green-800 dark:text-green-200">
                        {t('pdfeditor.saving')}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Balance display with highlight animation */}
            <div className={getBalanceClassName()}>
              <span className="text-gray-700 dark:text-gray-300">Current Balance:</span>
              <motion.span 
                className="font-mono font-medium text-gray-900 dark:text-white"
                key={balance}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                ${balance.toLocaleString()}
              </motion.span>
            </div>

            {/* Transactions with highlight animations */}
            <div className="space-y-2">
              {/* Headers */}
              <div className="grid grid-cols-[45px_1fr_80px] sm:grid-cols-[60px_1fr_100px] gap-2 p-2 border-b border-gray-200 dark:border-gray-700">
                <span className="text-gray-500 dark:text-gray-400 font-mono text-xs sm:text-sm font-semibold">Date</span>
                <span className="text-gray-500 dark:text-gray-400 font-mono text-xs sm:text-sm font-semibold">Description</span>
                <span className="text-gray-500 dark:text-gray-400 font-mono text-xs sm:text-sm font-semibold text-right">Amount</span>
              </div>
              
              {transactions.map((transaction, index) => (
                <div key={`${transaction.desc}-${transaction.amount}`} className={getRowClassName(transaction)}>
                  <span className="text-gray-600 dark:text-gray-400 font-mono text-xs sm:text-sm">
                    {transaction.date}
                  </span>
                  <motion.span 
                    className="text-gray-900 dark:text-white font-mono text-xs sm:text-sm truncate"
                    key={transaction.desc}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {transaction.desc}
                  </motion.span>
                  <motion.span 
                    className={`text-right font-mono text-xs sm:text-sm ${
                      transaction.amount > 0 
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                    key={transaction.amount}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    {transaction.amount > 0 ? '+' : ''}{transaction.amount.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </motion.span>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
              <div>Statement Period: 10/01/2024 - 10/31/2024</div>

            </div>
          </motion.div>

          {/* Loading message when document is hidden */}
          <AnimatePresence>
            {!isDocumentVisible && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <div className="flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-lg px-4 py-2 shadow-lg">
                  <svg className="animate-spin h-5 w-5 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-gray-900 dark:text-white font-medium">
                    {t('pdfeditor.loading')}
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default DocumentComparison;